/**
 * useSpawnBatchCost Hook
 *
 * User Story: US-P7-001B - Variable Cost Foundation
 * Step 3: Spawn Batch Cost Calculation
 *
 * Provides hooks for:
 * - Calculate/preview spawn batch cost before creation
 * - Fetch cost for existing spawn batch
 * - Real-time cost updates as form changes
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call spawn-batches Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callSpawnBatchesFunction(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('spawn-batches', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook for calculating spawn batch cost (preview before creation)
 *
 * Provides:
 * - costResult: Current cost breakdown with FIFO estimates
 * - isLoading: Loading state
 * - error: Error state
 * - calculateCost: Trigger cost calculation with parameters
 * - clearCost: Reset cost result
 *
 * @param {Object} options - Optional default parameters
 * @returns {Object} - Hook state and methods
 */
export function useSpawnBatchCost(/* options = {} */) {
  const [costResult, setCostResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Calculate spawn batch cost
   *
   * @param {Object} params - Cost calculation parameters
   * @param {string} params.grain_type - Type of grain (rye, millet, wheat, oat, sorghum)
   * @param {number} params.grain_weight_kg - Total grain weight in kg
   * @param {number} params.bag_count - Number of spawn bags
   * @param {string} params.bag_size - Bag size (1kg, 2.5kg, 1.5kg, 4.5kg)
   * @param {boolean} params.use_filters - Whether to include filter patch cost
   * @returns {Promise<Object>} - Cost calculation result
   */
  const calculateCost = useCallback(async (params) => {
    const {
      grain_type,
      grain_weight_kg,
      bag_count,
      bag_size,
      use_filters = false
    } = params

    // Validate required fields
    if (!grain_type || !grain_weight_kg || !bag_count || !bag_size) {
      const missingFields = []
      if (!grain_type) missingFields.push('grain_type')
      if (!grain_weight_kg) missingFields.push('grain_weight_kg')
      if (!bag_count) missingFields.push('bag_count')
      if (!bag_size) missingFields.push('bag_size')
      setError(`Missing required fields: ${missingFields.join(', ')}`)
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callSpawnBatchesFunction('calculate-cost', {
        grain_type,
        grain_weight_kg,
        bag_count,
        bag_size,
        use_filters
      })

      setCostResult(result.data)
      return result.data
    } catch (err) {
      console.error('[useSpawnBatchCost.calculateCost] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Clear cost result
   */
  const clearCost = useCallback(() => {
    setCostResult(null)
    setError(null)
  }, [])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => setError(null), [])

  return {
    // State
    costResult,
    isLoading,
    error,

    // Derived values (for convenience)
    grainCost: costResult?.breakdown?.grain_cost || 0,
    bagCost: costResult?.breakdown?.bag_cost || 0,
    filterCost: costResult?.breakdown?.filter_cost || 0,
    totalCost: costResult?.breakdown?.total_cost || 0,
    costPerBag: costResult?.breakdown?.cost_per_bag || 0,
    warnings: costResult?.warnings || [],
    inventorySufficient: costResult?.inventory_sufficient ?? true,

    // Methods
    calculateCost,
    clearCost,
    clearError
  }
}

/**
 * Hook for fetching cost of an existing spawn batch
 *
 * @param {string} batchId - Spawn batch ID (auto-fetches if provided)
 * @returns {Object} - Hook state and methods
 */
export function useExistingSpawnBatchCost(batchId = null) {
  const [batchCost, setBatchCost] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch cost for a spawn batch
   *
   * @param {string} id - Spawn batch ID
   * @returns {Promise<Object>} - Batch cost data
   */
  const fetchBatchCost = useCallback(async (id) => {
    if (!id) {
      setError('Batch ID is required')
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callSpawnBatchesFunction('get-cost', { id })
      setBatchCost(result.data)
      return result.data
    } catch (err) {
      console.error('[useExistingSpawnBatchCost.fetchBatchCost] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Refetch cost for current batch
   */
  const refetch = useCallback(() => {
    if (batchId) {
      return fetchBatchCost(batchId)
    }
    return null
  }, [batchId, fetchBatchCost])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => setError(null), [])

  // Auto-fetch on mount if batchId provided
  useEffect(() => {
    if (batchId) {
      // Catch errors silently - they're stored in error state
      fetchBatchCost(batchId).catch(() => {
        // Error already handled in fetchBatchCost
      })
    }
  }, [batchId, fetchBatchCost])

  return {
    // State
    batchCost,
    isLoading,
    error,

    // Derived values
    grainCost: batchCost?.costs?.grain_cost || 0,
    consumablesCost: batchCost?.costs?.consumables_cost || 0,
    totalCost: batchCost?.costs?.total_cost || 0,
    costPerBag: batchCost?.costs?.cost_per_bag || 0,
    hasCosts: batchCost?.has_costs || false,

    // Methods
    fetchBatchCost,
    refetch,
    clearError
  }
}

/**
 * Hook for real-time cost calculation as form values change
 * Debounces API calls to avoid excessive requests
 *
 * @param {Object} formValues - Form values to calculate cost for
 * @param {Object} options - Options
 * @param {number} options.debounceMs - Debounce delay in ms (default: 300)
 * @param {boolean} options.enabled - Whether to auto-calculate (default: true)
 * @returns {Object} - Hook state
 */
export function useSpawnBatchCostPreview(formValues = {}, options = {}) {
  const { debounceMs = 300, enabled = true } = options
  const [costResult, setCostResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const debounceRef = useRef(null)
  const prevValuesRef = useRef(null)

  // Extract form values (handle null/undefined)
  const safeFormValues = formValues || {}
  const { grain_type, bag_weight_kg, bag_count, use_filters, grain_weight_per_bag_kg } = safeFormValues

  // Calculate grain weight from recipe grain weight (accurate) or bag weight (legacy fallback)
  const effectiveGrainPerBag = grain_weight_per_bag_kg || bag_weight_kg
  const grain_weight_kg = effectiveGrainPerBag && bag_count ? effectiveGrainPerBag * bag_count : 0

  // Map bag weight to bag size string
  const getBagSize = (weight) => {
    if (!weight) return null
    const weightNum = Number(weight)
    if (weightNum === 1) return '1kg'
    if (weightNum === 1.5) return '1.5kg'
    if (weightNum === 2.5) return '2.5kg'
    if (weightNum === 4.5) return '4.5kg'
    return `${weightNum}kg`
  }

  const bag_size = getBagSize(bag_weight_kg)

  useEffect(() => {
    // Check if we have all required values
    const hasRequiredValues = grain_type && grain_weight_kg > 0 && bag_count > 0 && bag_size

    if (!enabled || !hasRequiredValues) {
      return
    }

    // Check if values have changed
    const currentValues = JSON.stringify({ grain_type, grain_weight_kg, bag_count, bag_size, use_filters })
    if (currentValues === prevValuesRef.current) {
      return
    }
    prevValuesRef.current = currentValues

    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Set up new debounced call
    debounceRef.current = setTimeout(async () => {
      try {
        setIsLoading(true)
        setError(null)

        const result = await callSpawnBatchesFunction('calculate-cost', {
          grain_type,
          grain_weight_kg,
          bag_count,
          bag_size,
          use_filters: use_filters || false
        })

        setCostResult(result.data)
      } catch (err) {
        console.error('[useSpawnBatchCostPreview] Error:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }, debounceMs)

    // Cleanup
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [grain_type, grain_weight_kg, bag_count, bag_size, use_filters, debounceMs, enabled])

  return {
    // State
    costResult,
    isLoading,
    error,

    // Derived values
    grainCost: costResult?.breakdown?.grain_cost || 0,
    bagCost: costResult?.breakdown?.bag_cost || 0,
    filterCost: costResult?.breakdown?.filter_cost || 0,
    totalCost: costResult?.breakdown?.total_cost || 0,
    costPerBag: costResult?.breakdown?.cost_per_bag || 0,
    warnings: costResult?.warnings || [],
    inventorySufficient: costResult?.inventory_sufficient ?? true,

    // Raw allocations (for detailed view)
    grainAllocations: costResult?.breakdown?.grain_allocations || [],
    bagAllocations: costResult?.breakdown?.bag_allocations || [],
    filterAllocations: costResult?.breakdown?.filter_allocations || []
  }
}

export default useSpawnBatchCost
